import { useState, useContext, useEffect, useRef, useMemo } from 'react';
import { makeStyles } from '@mui/styles';
import { useHistory } from 'react-router';
import IdleTimer from 'react-idle-timer';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MenuIcon from '@mui/icons-material/Menu';
import {
    AppBar,
    Avatar,
    Box,
    CssBaseline,
    Drawer,
    IconButton,
    Menu,
    MenuItem,
    Toolbar,
    Typography,
    useMediaQuery
} from '@mui/material';
// Our Components
import { AuthContext } from 'context/auth-context';
import { CardProvider } from 'context/cards/cardContext';
import { LoaderContext } from 'components/shared/Loader/LoaderContext';
import { ModalApprovedAccount } from '../../components/modalKYX/WelcomeExperiencie/ApprovedModal';
import { ModalPreKycWelcome } from '../../components/modalKYX/PreKYCWelcome/PreKycWelcome';
import { SidebarMenu } from '../SidebarMenu/SidebarMenu';
import { StartKYX } from 'components/modalKYX/StartKYX';
import KYXProvider from 'context/kyx-context';
import LayoutRoutes from 'routes/layout.routes';
import Loader from 'components/shared/Loader/Loader';
import ReconnectBankComponent from 'components/shared/ReconnectBank.component';
import UpgradeBusinessBanner from 'components/shared/UpgradeBusinessBanner';
// Services
import { Users } from 'lib/api';
// Utils and Helpers
import { APP_ROUTES, LAYOUT_ROUTES } from 'routes';
import { BUSINESS_FUNDING_TYPE, KYB_STATUS, KYC_STATUS, MUI_CLOSE_MODAL_REASONS, USER_ROLES } from 'helpers/constants';
import { isAdminUser, isCardHolder } from 'helpers/auth';
import { RemoveTokenFromLS as removeTokenFromLS } from 'resolvers/RemoveTokenFromLS';
// Assets
import legalNameLogo from 'assets/defaultLayout/legalNameLogo.svg';
import logo from 'assets/defaultLayout/psLogoWhite.png';

const TIMEOUT_TIME_IN_MS = 1000 * 60 * 60 * 2; // 2 Hours
const SIDEBAR_WIDTH = 240;

const useStyles = makeStyles(() => ({
    content: {
        width: '100%'
    },
    container: {
        padding: '30px'
    },
    toolbar: {
        columnGap: '.5em'
    },
    paper: {
        backgroundColor: '#182D55 !important',
        color: 'white!important'
    },
    logo: {
        width: '10em'
    },
    userinfo: {
        display: 'flex',
        alignItems: 'center',
        columnGap: '.5em',
        cursor: 'pointer'
    },
    toolbarLogo: {
        marginTop: '30px',
        marginBottom: '40px',
        cursor: 'pointer'
    }
}));

export default function Layout({ children }) {
    const matches = useMediaQuery('(min-width:900px)');
    const history = useHistory();
    const classes = useStyles();
    const { removeToken, user, userInfo, businessInfo, setDecodeState, setUserInfo, setBusinessInfo } = useContext(AuthContext);
    const { isLoading } = useContext(LoaderContext);
    const [anchorEl, setAnchorEl] = useState(null);
    const [isPostKybModalOpen, setIsPostKYBModalOpen] = useState(true);
    const [isPreKYCModalOpen, setPreKYCModalOpen] = useState(true);
    const [isSidebarMenuOpen, setIsSidebarMenuOpen] = useState(false);
    const [kybStatus, setKYBStatus] = useState({
        bgColor: '',
        lblColor: '',
        label: '',
        status: ''
    });
    const [isKYXModalOpen, setIsKYXModalOpen] = useState(false);
    const [currentKYCStep, setCurrentKYCStep] = useState(0);
    const idleTimerRef = useRef(null);
    const hasKYC = useMemo(() => Object.values(KYC_STATUS).includes(userInfo.kyc), [userInfo.kyc]);
    const hasKYB = !!userInfo.kyb && businessInfo.id && businessInfo.owners?.length > 0 && businessInfo.banks?.length > 0;

    useEffect(() => {
        if (businessInfo.loaded) {
            const openModal = () => {
                if (!userInfo.is_business_creator) {
                    return;
                }
                if ((!hasKYC || !hasKYB) && userInfo.has_welcome_experience_pre_kyc) {
                    setIsKYXModalOpen(true);
                } else {
                    setIsKYXModalOpen(false);
                }
            };
            openModal();
        }
    }, [businessInfo.loaded, hasKYB, hasKYC, userInfo.is_business_creator, userInfo.has_welcome_experience_pre_kyc]);

    useEffect(() => {
        if (userInfo.kyb === KYB_STATUS.APPROVED && !userInfo.account) {
            if (businessInfo.owners?.length > 0 && businessInfo.banks?.length > 0) {
                setKYBStatus({
                    bgColor: '#F8EBDD',
                    lblColor: '#FF9E43',
                    status: 'UNDER REVIEW',
                    label: 'UNDER REVIEW'
                });
                return;
            }
            setKYBStatus({
                bgColor: '#F1F3F7',
                lblColor: '#7D95A5',
                status: 'INCOMPLETE',
                label: 'Complete Now'
            });
            return;
        }
        if (!hasKYC) {
            setKYBStatus({
                bgColor: '#F1F3F7',
                lblColor: '#7D95A5',
                status: 'INCOMPLETE',
                label: 'Complete Now'
            });
            return;
        }
        switch (userInfo.kyb) {
            case KYB_STATUS.NOT_SUBMITTED:
                setKYBStatus({
                    bgColor: '#F1F3F7',
                    lblColor: '#7D95A5',
                    status: 'INCOMPLETE',
                    label: 'Complete Now'
                });
                break;
            case KYB_STATUS.NOT_STARTED:
                setKYBStatus({
                    bgColor: '#F1F3F7',
                    lblColor: '#7D95A5',
                    status: 'INCOMPLETE',
                    label: 'Complete Now'
                });
                break;
            case KYB_STATUS.STARTED:
                setKYBStatus({
                    bgColor: '#F8EBDD',
                    lblColor: '#FF9E43',
                    status: 'STARTED',
                    label: 'Finish '
                });
                break;
            case KYB_STATUS.INCOMPLETE:
                setKYBStatus({
                    bgColor: '#F1F3F7',
                    lblColor: '#7D95A5',
                    status: 'INCOMPLETE',
                    label: 'Complete Now'
                });
                break;
            case KYB_STATUS.DECLINED:
                setKYBStatus({
                    bgColor: '#FBD8E3',
                    lblColor: '#F65570',
                    status: 'DECLINED',
                    label: 'Contact Support'
                });
                break;
            case KYB_STATUS.APPROVED:
                setKYBStatus({
                    bgColor: '#E2EFD8',
                    lblColor: '#77B44B',
                    status: 'VERIFIED',
                    label: 'Hide'
                });
                break;
            case KYB_STATUS.REVIEW:
                setKYBStatus({
                    bgColor: '#F8EBDD',
                    lblColor: '#FF9E43',
                    status: 'UNDER REVIEW'
                });
                break;

            default:
                setKYBStatus({
                    bgColor: '#F1F3F7',
                    lblColor: '#7D95A5',
                    status: 'INCOMPLETE',
                    label: 'Complete Now'
                });
                break;
        }
    }, [businessInfo.banks?.length, businessInfo.owners?.length, hasKYC, userInfo]);

    const handleMenu = event => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const toggleSidebar = () => {
        setIsSidebarMenuOpen(!isSidebarMenuOpen);
    };

    const handleSavePreKyc = async (e, reason) => {
        if (MUI_CLOSE_MODAL_REASONS.includes(reason)) {
            return;
        }
        try {
            await Users.submitPreKyc();
            setUserInfo({
                ...userInfo,
                has_welcome_experience_pre_kyc: true
            });
            setPreKYCModalOpen(false);
        } catch (error) {
            console.error(error);
        }
    };

    const handlePostKYBClose = async (e, reason) => {
        if (MUI_CLOSE_MODAL_REASONS.includes(reason)) {
            return;
        }
        try {
            await Users.submitPostApproveKyb();
            setUserInfo({
                ...userInfo,
                has_welcome_experience_post_kyb: true
            });
            history.push(LAYOUT_ROUTES.CARDS);
            setIsPostKYBModalOpen(false);
        } catch (error) {
            console.error(error);
        }
    };

    const handleIdleTimeout = () => {
        removeToken();
        removeTokenFromLS();
        window.localStorage.removeItem('kyx_hidden');
        setUserInfo({
            loaded: false
        });
        setBusinessInfo({
            loaded: false,
            owners: [],
            banks: []
        });
        setDecodeState({
            loaded: false
        });
        history.push(APP_ROUTES.LOGIN);
    };

    const isBankDisconnected = userInfo?.bank_need_reconnect && isAdminUser(userInfo?.role?.id) ? true : false;
    const isGiftCardUser = userInfo?.is_giftcard_only && isCardHolder(userInfo?.role?.id) ? true : false;

    return (
        <IdleTimer ref={idleTimerRef} timeout={TIMEOUT_TIME_IN_MS} onIdle={handleIdleTimeout}>
            <KYXProvider value={{ setIsKYXModalOpen, isKYXModalOpen, currentKYCStep, setCurrentKYCStep }}>
                <StartKYX />
                <Box sx={{ display: 'flex', bgcolor: '#F2F2F2' }}>
                    <CssBaseline />
                    <AppBar
                        position="fixed"
                        sx={{
                            width: {
                                lg: `calc(100% - ${SIDEBAR_WIDTH}px)`,
                                sm: '100%'
                            },
                            zIndex: 2,
                            ...(businessInfo?.is_frozen && { top: '46px' })
                        }}
                        style={{ background: 'white', color: 'black', boxShadow: 'none' }}>
                        <Toolbar
                            sx={{
                                justifyContent: {
                                    lg: 'right',
                                    md: 'right',
                                    sm: 'space-between',
                                    xs: 'space-between'
                                }
                            }}
                            className={classes.toolbar}>
                            <IconButton
                                edge="start"
                                color="inherit"
                                aria-label="open drawer"
                                sx={{
                                    display: { lg: 'none', md: 'none' }
                                }}
                                onClick={toggleSidebar}>
                                <MenuIcon />
                            </IconButton>
                            <Box
                                sx={{
                                    margin: '1em',
                                    padding: '8px 4px',
                                    borderRadius: '4px',
                                    backgroundColor: kybStatus.bgColor,
                                    display: {
                                        lg: 'none',
                                        md: 'none',
                                        sm: 'flex',
                                        xs: 'flex'
                                    },
                                    justifyContent: 'center'
                                }}>
                                <Typography
                                    sx={{
                                        fontWeight: 'bold',
                                        fontSize: '12px',
                                        lineHeight: '16px',
                                        color: kybStatus.lblColor
                                    }}>
                                    {kybStatus.status}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', marginLeft: 'auto' }}>
                                <div className={classes.userinfo} onClick={handleMenu}>
                                    <Avatar>{user?.fullName[0] || ''}</Avatar>
                                    <Typography marginLeft="5px">{user?.fullName || ''}</Typography>
                                    <IconButton onClick={handleMenu}>
                                        <KeyboardArrowDownIcon />
                                    </IconButton>
                                </div>
                            </Box>

                            {(userInfo.is_business_creator &&
                                userInfo.account &&
                                userInfo?.role?.id === USER_ROLES.ADMIN &&
                                !userInfo?.has_welcome_experience_post_kyb &&
                                businessInfo.funding_type === BUSINESS_FUNDING_TYPE.CREDIT_LINE && (
                                    <CardProvider>
                                        <ModalApprovedAccount open={isPostKybModalOpen} handleClose={handlePostKYBClose} />
                                    </CardProvider>
                                )) ||
                                null}

                            {(userInfo.is_business_creator &&
                                userInfo?.role?.id === USER_ROLES.ADMIN &&
                                !userInfo.has_welcome_experience_pre_kyc &&
                                !userInfo.phone_number && (
                                    <ModalPreKycWelcome open={isPreKYCModalOpen} handlePreKyc={handleSavePreKyc} />
                                )) ||
                                null}

                            <Menu
                                id="menu-appbar"
                                anchorEl={anchorEl}
                                anchorOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right'
                                }}
                                keepMounted
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right'
                                }}
                                open={Boolean(anchorEl)}
                                onClose={handleClose}>
                                <MenuItem onClick={() => history.push(LAYOUT_ROUTES.PROFILE)}>Profile</MenuItem>
                                <MenuItem onClick={() => handleIdleTimeout()}>Log out</MenuItem>
                            </Menu>
                        </Toolbar>
                    </AppBar>
                    {matches ? (
                        <Drawer
                            classes={{ paper: classes.paper }}
                            sx={{
                                width: SIDEBAR_WIDTH,
                                flexShrink: 0,
                                '& .MuiDrawer-paper': {
                                    width: SIDEBAR_WIDTH,
                                    boxSizing: 'border-box',
                                    ...(businessInfo?.is_frozen && { top: '46px' })
                                },
                                zIndex: 2
                            }}
                            variant="permanent"
                            anchor="left">
                            <Toolbar
                                onClick={() => history.push(APP_ROUTES.DASHBOARD)}
                                style={{ margin: '0px' }}
                                className={classes.toolbarLogo}>
                                <img draggable={false} alt={logo} src={logo} className={classes.logo} />
                            </Toolbar>
                            {userInfo.id_role !== 3 && (
                                <Box style={{ padding: '30px' }}>
                                    <img style={{ marginLeft: '-5px', marginBottom: '4px' }} src={legalNameLogo} alt="" />
                                    <strong
                                        style={{
                                            display: 'inline-block',
                                            maxWidth: '80%',
                                            marginLeft: '20px',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            fontSize: '14px'
                                        }}>
                                        {businessInfo.legal_name}
                                    </strong>
                                </Box>
                            )}
                            <SidebarMenu />
                        </Drawer>
                    ) : (
                        <Drawer
                            classes={{ paper: classes.paper }}
                            sx={{
                                width: SIDEBAR_WIDTH,
                                flexShrink: 0,
                                '& .MuiDrawer-paper': {
                                    width: SIDEBAR_WIDTH,
                                    boxSizing: 'border-box',
                                    ...(businessInfo?.is_frozen && { top: '46px' })
                                },
                                zIndex: 2
                            }}
                            variant="temporary"
                            anchor="left"
                            open={isSidebarMenuOpen}
                            onClose={toggleSidebar}>
                            <Toolbar
                                onClick={() => history.push(APP_ROUTES.DASHBOARD)}
                                style={{ margin: '10px' }}
                                className={classes.toolbarLogo}>
                                <img draggable={false} alt={logo} src={logo} className={classes.logo} />
                            </Toolbar>
                            {userInfo.id_role !== 3 && (
                                <Box style={{ padding: '30px' }}>
                                    <img style={{ marginLeft: '-5px', marginBottom: '4px' }} src={legalNameLogo} alt="" />
                                    <strong
                                        style={{
                                            display: 'inline-block',
                                            maxWidth: '80%',
                                            marginLeft: '20px',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden'
                                        }}>
                                        {businessInfo.legal_name}
                                    </strong>
                                </Box>
                            )}
                            <SidebarMenu />
                        </Drawer>
                    )}
                    <Box
                        component="main"
                        sx={{ flexGrow: 1, bgcolor: '#F2F2F2', ...(businessInfo?.is_frozen && { marginTop: '40px' }) }}>
                        <Toolbar />
                        {isLoading && <Loader />}
                        {isBankDisconnected && <ReconnectBankComponent idBank={userInfo.bank_need_reconnect} />}
                        <main className={classes.content}>
                            <Box maxWidth="xl" className={classes.container}>
                                {isGiftCardUser && <UpgradeBusinessBanner />}
                                <LayoutRoutes />
                            </Box>
                        </main>
                    </Box>
                </Box>
            </KYXProvider>
        </IdleTimer>
    );
}
